import { Component, Input, OnInit } from "@angular/core";
import { MatSort } from "@angular/material/sort";

/**
 * Sort control for the phone card layouts.
 *
 * Why this exists (Hayden, 2026-09-14): "in teleconnex campaign I used to be able to tap the
 * top and filter campaign by most leads delivered to least and vice versa. It was very handy."
 * The mobile card layout shipped 2026-09-07 turns each table row into a card, and to do that it
 * hides the header row - which is exactly what he was tapping. Sorting itself never broke; the
 * control simply left the screen on phones. This puts it back in a form that suits a thumb.
 *
 * It drives the table's existing MatSort rather than sorting a copy of the data, so the phone and
 * the desktop stay in step and there is one sorting implementation, not two.
 *
 * Usage - the column ids must match the table's matColumnDef names:
 *   <kt-mobile-sort [sort]="sort"
 *                   [columns]="[{ id: 'totalcount', label: 'Unique Leads' },
 *                               { id: 'sumofleads', label: 'Total Revenue' }]"
 *                   defaultColumn="totalcount"></kt-mobile-sort>
 */
export interface MobileSortColumn {
  /** matColumnDef name on the table */
  id: string;
  /** what the user sees in the dropdown */
  label: string;
}

@Component({
  selector: "kt-mobile-sort",
  standalone: false,
  templateUrl: "./mobile-sort.component.html",
  styleUrls: ["./mobile-sort.component.scss"],
})
export class MobileSortComponent implements OnInit {
  /** The table's MatSort. Pass the same instance assigned to dataSource.sort. */
  @Input() sort: MatSort;
  @Input() columns: MobileSortColumn[] = [];
  /** Column to select on load. Falls back to the first column given. */
  @Input() defaultColumn = "";
  /** Starting direction. Most-first is the useful default for leads and revenue. */
  @Input() defaultDirection: "asc" | "desc" = "desc";

  active = "";
  direction: "asc" | "desc" = "desc";

  ngOnInit(): void {
    this.active =
      this.defaultColumn || (this.columns.length ? this.columns[0].id : "");
    this.direction = this.defaultDirection;
    // Reflect the control's initial state on load without sorting the table, so the page still
    // opens in the order the API returned (newest-first, Total pinned) until the user asks.
    if (this.sort && this.active) {
      this.sort.active = this.active;
      this.sort.direction = this.direction;
    }
  }

  onColumnChange(id: string): void {
    this.active = id;
    this.apply();
  }

  toggleDirection(): void {
    this.direction = this.direction === "desc" ? "asc" : "desc";
    this.apply();
  }

  /** Label for the current direction, phrased for the column rather than generically. */
  get directionLabel(): string {
    return this.direction === "desc" ? "Most first" : "Least first";
  }

  private apply(): void {
    if (!this.sort || !this.active) {
      return;
    }
    // Setting active/direction and emitting sortChange is what MatTableDataSource listens to.
    // Calling sort.sort() instead would advance MatSort's own asc -> desc -> none cycle and
    // fight the explicit direction chosen here.
    this.sort.active = this.active;
    this.sort.direction = this.direction;
    this.sort.sortChange.emit({
      active: this.active,
      direction: this.direction,
    });
  }
}
